import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@hotjar/hotsauce';
import { markEmailVerified, sendVerificationEmail } from '@hotjar/webapp/domains/mfa/api';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { EmailVerificationResult } from './types';

export enum SendEmailError {
  TooManyAttempts = 'error-too-many-requests',
  Other = 'error',
}

export enum MarkEmailVerifiedError {
  WrongCode = 'error-wrong-code',
  UnretriableError = 'error-unretriable',
  Other = 'error',
}

export const useEmailVerificationComponent = (
  onEmailVerified: (result: EmailVerificationResult) => void,
  onLoadingChange: (isLoading: boolean) => void,
): {
  isSendEmailPending: boolean;
  sendEmailError: SendEmailError | null;
  isSuccess: boolean;
  verificationId: string;
  hasVerifyError: boolean;
  handleVerificationCodeChange: (code: string) => void;
  handleOnVerificationCodeInput: () => void;
  markEmailVerifiedError: MarkEmailVerifiedError | null;
  doSendVerificationEmail: () => void;
} => {
  const { t } = useTranslationWithPrefix('auth:verify-email-component');
  const { i18n } = useTranslation();
  const currentLocale = i18n.languages?.[0];

  const [verificationId, setVerificationId] = useState<string>('');
  const [hasVerifyError, setHasVerifyError] = useState<boolean>(false);
  const [isSendEmailPending, setIsSendEmailPending] = useState<boolean>(false);
  const [sendEmailError, setSendEmailError] = useState<SendEmailError | null>(null);
  const [markEmailVerifiedError, setMarkEmailVerifiedError] =
    useState<MarkEmailVerifiedError | null>(null);

  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const { showToast } = useNotify();

  const doSendVerificationEmail = useCallback(() => {
    setIsSendEmailPending(true);
    sendVerificationEmail({
      locale: currentLocale,
    })
      .then((responseModel) => {
        setVerificationId(responseModel.verificationId);
      })
      .catch((error) => {
        if (error.response && error.response.status === 409) {
          // Email already verified, let's move on...
          onEmailVerified(EmailVerificationResult.AlreadyVerified);
        } else if (error.response && error.response.status === 400) {
          setSendEmailError(SendEmailError.TooManyAttempts);
        } else {
          setSendEmailError(SendEmailError.Other);
        }
      })
      .finally(() => {
        setIsSendEmailPending(false);
      });
  }, [onEmailVerified]);

  useEffect(() => {
    // Send verification email immediately on first render
    doSendVerificationEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVerificationCodeChange = useCallback(
    (code: string) => {
      onLoadingChange(true);
      markEmailVerified({
        code,
        vid: verificationId,
      })
        .then(() => {
          onEmailVerified(EmailVerificationResult.VerifiedFirstTime);
          setIsSuccess(true);
          showToast({ text: t('verify-email-success'), kind: 'success' });
        })
        .catch((error) => {
          setHasVerifyError(true);
          if (error.response && error.response.status === 400) {
            // eslint-disable-next-line
            switch (error.response.data.error_type) {
              case 'WrongCodeError':
                setMarkEmailVerifiedError(MarkEmailVerifiedError.WrongCode);
                break;
              case 'InvalidTokenError':
              case 'UnknownVerificationIdError':
              case 'UserAlreadyExists':
                setMarkEmailVerifiedError(MarkEmailVerifiedError.UnretriableError);
                break;
              default:
                setMarkEmailVerifiedError(MarkEmailVerifiedError.Other);
                break;
            }
          } else {
            setMarkEmailVerifiedError(MarkEmailVerifiedError.Other);
          }
        })
        .finally(() => {
          onLoadingChange(false);
        });
    },
    [verificationId, onEmailVerified, t, showToast, onLoadingChange],
  );

  const handleOnVerificationCodeInput = useCallback(() => {
    // Clear errors
    setHasVerifyError(false);
    setMarkEmailVerifiedError(null);
  }, [setHasVerifyError]);

  return {
    isSendEmailPending,
    sendEmailError,
    isSuccess,
    verificationId,
    hasVerifyError,
    handleVerificationCodeChange,
    handleOnVerificationCodeInput,
    markEmailVerifiedError,
    doSendVerificationEmail,
  };
};
